import {
  FormControlLabel,
  FormControl,
  RadioGroup,
  Radio,
  MenuItem,
} from "@material-ui/core";
import React from "react";
import C1InputField from "app/c1component/C1InputField";
import C1SelectField from "app/c1component/C1SelectField";

const RaeoAppAssessmentItem = ({
  assess,
  isDisabled,
  errors,
  handleInputChangeCommon,
  handleCheckboxChange,
}) => {
  let fieldConfig = {};
  let fieldType = "TEXT";

  if (assess?.TSctRaeoMstAssessment?.asmFieldAttribute) {
    try {
      fieldConfig = JSON.parse(assess.TSctRaeoMstAssessment.asmFieldAttribute);
      fieldType = fieldConfig.fieldType || "TEXT";
    } catch (e) {
      fieldConfig = {};
      fieldType = "TEXT";
    }
  }

  const getResponseObject = () => {
    const raw = assess?.apasResponse || "";
    if (!raw) return {};

    try {
      return JSON.parse(raw);
    } catch (e) {
      // backward compatibility with old plain text / Y:reason format
      if (raw.includes(":")) {
        const [value, explanation] = raw.split(/:(.*)/s);
        return { value: value || "", explanation: explanation || "" };
      }
      return { value: raw };
    }
  };

  const responseObj = getResponseObject();

  const updateResponseObject = (patch) => {
    const next = { ...responseObj, ...patch };
    handleInputChangeCommon(
      {
        target: {
          name: "apasResponse",
          value: JSON.stringify(next),
        },
      },
      assess.apasId
    );
  };

  const getError = () =>
    errors[`TSctRaeoAppAssessments[${assess.apasId}].apasResponse`] || "";

  const hasError =
    errors[`TSctRaeoAppAssessments[${assess.apasId}].apasResponse`] !== undefined;

  const TextComponent = () => {
    return (
      <C1InputField
        label="Response"
        name="apasResponse"
        value={responseObj.value || ""}
        multiline
        rows={3}
        required
        disabled={isDisabled}
        onChange={(e) => updateResponseObject({ value: e.target.value })}
        inputProps={{ maxLength: fieldConfig.maxLength || 1024 }}
        error={hasError}
        helperText={getError()}
      />
    );
  };

  const NumComponent = () => {
    return (
      <C1InputField
        label="Response"
        name="apasResponse"
        value={responseObj.value || ""}
        required
        type="number"
        disabled={isDisabled}
        onChange={(e) => updateResponseObject({ value: e.target.value })}
        inputProps={{
          maxLength: 12,
          min: fieldConfig.min ?? "0",
          max: fieldConfig.max ?? "999999999999.99",
        }}
        error={hasError}
        helperText={getError()}
      />
    );
  };

  const renderFollowUps = () => {
    const followUps = Array.isArray(fieldConfig.followUps)
      ? fieldConfig.followUps
      : [];

    const selectedValue = responseObj.value || "";

    return followUps.map((fu, idx) => {
      const showWhen = Array.isArray(fu.showWhen)
        ? fu.showWhen
        : (fu.showWhen ? [fu.showWhen] : null);

      const shouldShow =
        !showWhen || showWhen.includes(selectedValue);

      if (!shouldShow) return null;

      const fuName = fu.name || "explanation";

      const requiredWhen = Array.isArray(fu.requiredWhen)
        ? fu.requiredWhen
        : (fu.requiredWhen ? [fu.requiredWhen] : []);

      const isRequired = requiredWhen.includes(selectedValue);

      if (fu.type === "TEXTAREA") {
        return (
          <div key={idx} style={{ width: "100%", marginTop: 8 }}>
            <C1InputField
              label={fu.label || "Explanation"}
              name={fuName}
              value={responseObj[fuName] || ""}
              multiline
              rows={fu.rows || 3}
              fullWidth
              required={isRequired}
              disabled={isDisabled}
              onChange={(e) =>
                updateResponseObject({
                  [fuName]: e.target.value,
                })
              }
              inputProps={{ maxLength: fu.maxLength || 1024 }}
              error={hasError}
              helperText={getError()}
            />
          </div>
        );
      }

      return (
        <div key={idx} style={{ width: "100%", marginTop: 8 }}>
          <C1InputField
            label={fu.label || "Explanation"}
            name={fuName}
            value={responseObj[fuName] || ""}
            fullWidth
            required={isRequired}
            disabled={isDisabled}
            onChange={(e) =>
              updateResponseObject({
                [fuName]: e.target.value,
              })
            }
            inputProps={{ maxLength: fu.maxLength || 255 }}
            error={hasError}
            helperText={getError()}
          />
        </div>
      );
    });
  };

  const ChoiceRadioComponent = () => {

    const options = Array.isArray(fieldConfig.options) && fieldConfig.options.length > 0
      ? fieldConfig.options
      : [
        { value: "Y", label: "Yes" },
        { value: "N", label: "No" },
      ];

    return (
      <FormControl component="fieldset" style={{ width: "100%" }}>
        <RadioGroup
          row
          aria-label="apasResponse"
          name="apasResponse"
          value={responseObj.value || ""}
          onChange={(e) => updateResponseObject({ value: e.target.value })}
        >
          {options.map((opt, idx) => (
            <FormControlLabel
              key={idx}
              value={opt.value}
              control={<Radio color="primary" />}
              label={opt.label}
              disabled={isDisabled}
            />
          ))}
        </RadioGroup>
        {renderFollowUps()}
      </FormControl>
    );
  };

  const ChoiceSelectComponent = () => {

    const options = Array.isArray(fieldConfig.options) ? fieldConfig.options : [];

    return (
      <>
        <C1SelectField
          label="Response"
          name="apasResponse"
          value={responseObj.value || ""}
          disabled={isDisabled}
          onChange={(e) => updateResponseObject({ value: e.target.value })}
          error={hasError}
          helperText={getError()}
        >
          {options.map((opt, idx) => (
            <MenuItem key={idx} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </C1SelectField>
        {renderFollowUps()}
      </>
    );
  };

  let comp = null;

  if (assess?.TSctRaeoMstAssessment?.asmIsQuestion !== 0) {
    switch (fieldType) {
      case "NUM":
        comp = NumComponent();
        break;

      case "RADIO":
        comp = ChoiceRadioComponent();
        break;

      case "RADIOEXT":
        comp = ChoiceRadioComponent();
        break;

      case "CHOICE":
        comp =
          (fieldConfig.render || "RADIO") === "SELECT"
            ? ChoiceSelectComponent()
            : ChoiceRadioComponent();
        break;

      case "SELECT":
        comp = ChoiceSelectComponent();
        break;

      case "TEXT":
      default:
        comp = TextComponent();
        break;
    }
  }

  return <>{comp}</>;
};

export default RaeoAppAssessmentItem;
